import styled from '@emotion/styled';
import {
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';

import {
  SelectedCheckbox,
  SelectedRadio,
  UnselectedCheckbox,
  UnselectedRadio,
} from '../../icons';
import {
  gray100Color,
  gray300Color,
  textQuaternaryColor,
  textTeriaryColor,
} from '../tokenColorTemporary';

import type { ToggleButtonGroupProps } from '@mui/material';
import type { Dispatch, ReactNode, SetStateAction } from 'react';

const EliceToggleButton = styled(ToggleButton)`
  border: none;
  border-radius: 1.25rem !important;
`;

export interface EliceSelectionProps extends ToggleButtonGroupProps {
  type: 'checkbox' | 'radio';
  options: { value: string; content: ReactNode | string }[];
  value: string[];
  setValue: Dispatch<SetStateAction<string[]>>;
  limitSelectCount?: number;
}

const EliceSelection = ({
  type,
  options,
  value,
  setValue,
  orientation = 'vertical',
  disabled,
  limitSelectCount = 1,
}: EliceSelectionProps) => {
  const theme = useTheme();

const handleSelectionChange = (
  event: React.MouseEvent<HTMLElement>,
  newValue: string | null | string[]
) => {
  if (!newValue || disabled) return;

  setValue(prevState => {
    const newValuesArray = Array.isArray(newValue) ? newValue : [newValue];
    const updatedValues: string[] = [...prevState];
    newValuesArray.forEach(value => {
      if (!updatedValues.includes(value)) {
        updatedValues.push(value);
      } else {
        updatedValues.splice(updatedValues.indexOf(value), 1);
      }
    });
    if (updatedValues.length > limitSelectCount) {
      return updatedValues.slice(-limitSelectCount);
    }
    return updatedValues;
  });
};

  return (
    <ToggleButtonGroup
      orientation={orientation}
      value={value}
      exclusive={type === 'radio'}
      onChange={handleSelectionChange}
      sx={{
        backgroundColor: 'transparent',
        gap: '1rem',
        '> button': {
          padding: '0 !important',
          '&:hover': {
            backgroundColor: 'transparent !important',
          },
        },
        '.Mui-selected': {
          backgroundColor: 'transparent !important',
          boxShadow: 'none !important',
        },
      }}
    >
      {options.map(option => {
        const isSelected = value.includes(option.value);
        return (
          <EliceToggleButton key={option.value} value={option.value}>
            <Stack
              direction="row"
              border="1px solid"
              borderColor={
                isSelected ? theme.palette.secondary.main : gray300Color
              }
              borderRadius="1.25rem"
              padding="1rem"
              gap="0.75rem"
              height="4rem"
              width="22rem"
              maxWidth="100%"
              boxSizing="border-box"
              alignItems="center"
              bgcolor={disabled ? gray100Color : '#ffffff'}
            >
              {isSelected && type === 'checkbox' && (
                <SelectedCheckbox color={theme.palette.secondary.main} />
              )}
              {!isSelected && type === 'checkbox' && (
                <UnselectedCheckbox color={gray300Color} />
              )}
              {isSelected && type === 'radio' && (
                <SelectedRadio color={theme.palette.secondary.main} />
              )}
              {!isSelected && type === 'radio' && (
                <UnselectedRadio color={gray300Color} />
              )}
              <Stack
                color={
                  isSelected
                    ? theme.palette.secondary.main
                    : disabled
                    ? textQuaternaryColor
                    : textTeriaryColor
                }
                fontSize="1.375rem"
                fontWeight={700}
              >
                {option.content}
              </Stack>
            </Stack>
          </EliceToggleButton>
        );
      })}
    </ToggleButtonGroup>
  );
};

export default EliceSelection;
